import { Theme, useMediaQuery } from "@biggeo/bg-ui";
import { Button, Grid, Stack, TabHeader } from "@biggeo/bg-ui/lab";
import {
    ActionKeyOutline,
    AddOutline,
    KeyOutline,
} from "@biggeo/bg-ui/lab/icons";
import * as A from "fp-ts/Array";
import { pipe } from "fp-ts/function";
import startCase from "lodash/startCase";
import { useState } from "react";
import { match } from "ts-pattern";

import SwitchToDatalabModal from "../../common/components/SwitchToDatalabModal";
import { useCloseModal, useOpenModal } from "../../common/redux/hooks";
import EnterAccessKeyContainer from "../../data-access-keys/containers/EnterAccessKeyContainer";
import MyDataContainer from "../containers/MyDataContainer";
import PurchasedDataContainer from "../containers/PurchasedDataContainer";
import { Routes } from "../../navigation/redux/model";
import GeneratedFilesContainer from "../containers/GeneratedFilesContainer";

export enum AvailableDataTabs {
    myData = "myData",
    availableData = "availableData",
    generatedFiles = "generatedFiles",
}

interface IAvailableDataPage {
    currentTab: AvailableDataTabs;
}

const AvailableDataPage = ({ currentTab }: IAvailableDataPage) => {
    const [reload, setReload] = useState<boolean>(false);
    const [tab, setTab] = useState<AvailableDataTabs>(
        currentTab ?? AvailableDataTabs.myData
    );
    const openModal = useOpenModal();
    const closeModal = useCloseModal();

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md")
    );

    const onAddOnDatalabClick = () => {
        openModal({
            modalType: "new-dialog",
            component: <SwitchToDatalabModal onClose={closeModal} />,
            dialogProps: {
                variant: "centered-mid-small",
            },
        });
    };

    const onUseAccessKey = () => {
        openModal({
            modalType: "new-dialog",
            component: (
                <EnterAccessKeyContainer
                    reload={reload}
                    setReload={setReload}
                />
            ),
            dialogProps: {
                variant: "centered-medium",
            },
        });
    };

    return (
        <Stack
            height="100%"
            width="100%"
            sx={{
                padding: 4,
            }}
        >
            <TabHeader
                hideScrollButtons="desktop-only"
                tag="Admin"
                currentTab={tab}
                tabs={pipe(
                    Object.values(AvailableDataTabs),
                    A.map((t) => ({
                        value: t,
                        onClick: () => {
                            setTab(t);
                        },
                        children: startCase(t),
                    }))
                )}
                cta={match(tab)
                    .with(AvailableDataTabs.myData, () => {
                        return (
                            <Button
                                startNode={<AddOutline size="xs" />}
                                onClick={() => onAddOnDatalabClick()}
                            >
                                {isMobile ? "Add" : "Add Data Source"}
                            </Button>
                        );
                    })
                    .with(AvailableDataTabs.availableData, () => {
                        return (
                            <Grid container spacing={2}>
                                <Grid item>
                                    <Button
                                        startNode={<KeyOutline size="xs" />}
                                        onClick={() => onUseAccessKey()}
                                        variant="outlined"
                                    >
                                        Enter Access Key
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        startNode={
                                            <ActionKeyOutline size="xs" />
                                        }
                                        href={Routes.marketplace}
                                        target="_blank"
                                    >
                                        Explore Marketplace
                                    </Button>
                                </Grid>
                            </Grid>
                        );
                    })
                    .with(AvailableDataTabs.generatedFiles, () => null)
                    .exhaustive()}
            />
            {match(tab)
                .with(AvailableDataTabs.myData, () => (
                    <MyDataContainer
                        onAddOnDatalabClick={onAddOnDatalabClick}
                    />
                ))
                .with(AvailableDataTabs.availableData, () => (
                    <PurchasedDataContainer reload={reload} />
                ))
                .with(AvailableDataTabs.generatedFiles, () => (
                    <GeneratedFilesContainer />
                ))
                .exhaustive()}
        </Stack>
    );
};

export default AvailableDataPage;
